import React, { useEffect, useState } from "react";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";
import { BookButton } from "@/components/site/BookButton";
import { Seo } from "@/components/site/Seo";
import { HoursList } from "@/components/site/HoursList";

export default function Contact() {
  const [s, setS] = useState(null);
  const [faqs, setFaqs] = useState([]);
  useEffect(() => {
    api.get("/content/settings").then((r) => setS(r.data)).catch(() => {});
    api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {});
  }, []);
  if (!s) return <div className="h-screen" />;

  return (
    <div>
      <Seo title="Contact & Location | Psalm 30:2 Chiropractic Gardner KS" description={`Visit Psalm 30:2 Chiropractic at ${s.address_line1}, ${s.address_line2}.`} />
      <section className="pt-36 lg:pt-48 pb-16 px-6 md:px-10 lg:px-14">
        <div className="max-w-[1500px] mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            {s.logo_url && (
              <img src={resolveImg(s.logo_url)} alt="Psalm 30:2 Chiropractic" className="h-16 w-auto object-contain mb-8" data-testid="contact-logo" />
            )}
            <div className="overline mb-6">Contact</div>
            <h1 className="font-serif font-light text-5xl lg:text-7xl leading-[1.0] text-espresso mb-10">Come see us.</h1>
            <div className="space-y-6">
              <a href={`https://maps.google.com/?q=${encodeURIComponent(s.map_query)}`} target="_blank" rel="noreferrer" className="flex gap-4 items-start group" data-testid="contact-address">
                <MapPin className="text-brass mt-1 shrink-0" strokeWidth={1.5} />
                <span className="text-lg font-light group-hover:text-taupe transition-colors">{s.address_line1}<br />{s.address_line2}</span>
              </a>
              <a href={`tel:${s.phone}`} className="flex gap-4 items-center group" data-testid="contact-phone">
                <Phone className="text-brass shrink-0" strokeWidth={1.5} />
                <span className="text-lg font-light group-hover:text-taupe transition-colors">{s.phone}</span>
              </a>
              <a href={`mailto:${s.email}`} className="flex gap-4 items-center group" data-testid="contact-email">
                <Mail className="text-brass shrink-0" strokeWidth={1.5} />
                <span className="text-lg font-light group-hover:text-taupe transition-colors">{s.email}</span>
              </a>
              <div className="flex gap-4 items-start">
                <Clock className="text-brass mt-1 shrink-0" strokeWidth={1.5} />
                <div className="w-full max-w-[300px]">
                  <HoursList hours={s.hours} rowClass="border-b border-espresso/5 font-light" valueClass="text-taupe" testid="contact-hours" />
                </div>
              </div>
            </div>
            <div className="mt-10 flex gap-4">
              <BookButton label="Book Online" variant="primary" track_label="contact" testid="contact-book-button" />
              <a href={`tel:${s.phone}`} className="inline-flex items-center px-7 py-3.5 text-xs uppercase tracking-[0.18em] border border-espresso/30 rounded-full hover:bg-linen transition-all" data-testid="contact-call-button">Call Us</a>
            </div>
          </div>
          <Reveal delay={120} className="lg:col-span-7">
            <div className="overflow-hidden rounded-sm h-full min-h-[420px]">
              <iframe
                title="Psalm 30:2 Chiropractic location"
                width="100%" height="100%" style={{ border: 0, minHeight: 420 }} loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps?q=${encodeURIComponent(s.map_query)}&output=embed`}
                data-testid="contact-map"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {faqs.length > 0 && (
        <section className="bg-linen py-24 px-6 md:px-10 lg:px-14" data-testid="contact-faqs">
          <div className="max-w-3xl mx-auto">
            <div className="overline mb-8">Frequently Asked</div>
            <div className="divide-y divide-espresso/10">
              {faqs.map((f) => (
                <div key={f.id || f.q} className="py-6">
                  <h3 className="font-serif text-2xl text-espresso mb-2">{f.q}</h3>
                  <p className="text-espressosoft/75 font-light leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
